import type {} from '@strata-monorepo/shared-types/src/window'
import { FieldInputted } from '../events/FieldInputted'
import { TrackingService } from '../services/TrackingService'

const USAGE_FIELD_NAME = 'estimatedMonthlyUsageKwh'
const USAGE_FIELD_LABEL = 'Estimated average monthly usage (kWh)'
const FORM_TYPE = 'consumption_output'

/**
 * Fallback only, for a caller that supplies no form name. Each surface passes
 * its own — the quiz the version its questionnaire config set, the grid the
 * name its other form events already beam — because `formName` is how this
 * project labels the surface an event came from, and without it nothing in the
 * payload says whether an estimate was produced by the quiz or the grid.
 */
const DEFAULT_FORM_NAME = 'fmp_v1'

/**
 * Written to `fieldSource`, which in this project names a value's provenance
 * ('manual', 'rentcast', 'dropdown'). The run distinction is folded into that
 * vocabulary rather than replacing it, so grouping by `fieldSource` keeps
 * answering one question and the provenance is not lost.
 */
const INITIAL_SOURCE = 'consumption-prediction-initial'
const RERUN_SOURCE = 'consumption-prediction-rerun'

const SESSION_GATE_KEY = 'strataUsagePredictionInitialFired'

export type UsageSource = typeof INITIAL_SOURCE | typeof RERUN_SOURCE

export type UsagePredictionSkipReason =
  | 'unsuccessful-response'
  | 'no-usage-value'
  | 'emitter-error'
  /** Tagular never received the row, so no event exists to have been emitted. */
  | 'tracking-unavailable'
  /** A newer prediction replaced this one before it could be reported. */
  | 'superseded'

export type UsagePredictionResult =
  | { emitted: true; usageSource: UsageSource; monthlyUsageKwh: number }
  | { emitted: false; reason: UsagePredictionSkipReason }

/**
 * Whether a skip reason is worth an error-tier log.
 *
 * Only two are: a successful prediction that carried no figure, and a broken
 * emitter. The rest describe outcomes the product already tolerates — a
 * non-success response, a page without Tagular, a prediction the caller
 * superseded — and raising those would put three error streams on a
 * non-exceptional path. Noisy error streams get muted, which would then hide
 * the `no-usage-value` signal this instrumentation exists for.
 *
 * Lives here so the surfaces that log it cannot drift from one another, and so
 * a reason added later has one place to declare its tier.
 */
export function isUsageTrackingFailure(reason: UsagePredictionSkipReason): boolean {
  return reason === 'no-usage-value' || reason === 'emitter-error'
}

export interface TrackUsagePredictionOptions {
  /** Established form id for the surface making the call. */
  formId?: string
  /**
   * The surface's own form name, which is what lets an analyst tell a
   * quiz-produced estimate from a grid-produced one, and joins these rows to
   * the rest of that surface's form events.
   */
  formName?: string
  /**
   * Asked immediately before emitting: is this response still the newest one?
   * A surface that can have several predictions in flight passes its own
   * staleness check here, so a superseded response neither reports a figure the
   * shopper never saw nor consumes the session's one initial slot. Omitted by
   * surfaces that cannot race.
   */
  isStillCurrent?: () => boolean
}

/**
 * Second layer of the once-per-session gate, in front of session storage.
 *
 * Session storage is the layer that matters, because the quiz and the plan grid
 * are separately-bundled widgets and only per-tab storage spans them. But it
 * throws when storage is blocked, and the Cohesion session id is unavailable
 * until Cohesion initializes — and treating "cannot prove it fired" as "has not
 * fired" would make every prediction in the session an initial fire. This
 * bounds that to one per bundle instead of one per prediction.
 *
 * Holds the session it fired for, not a boolean, so session rotation re-arms it
 * here just as it does in storage. One residual case is accepted: a prediction
 * that fires before Cohesion initializes is recorded against
 * `UNIDENTIFIED_SESSION`, so the first prediction after Cohesion arrives is
 * still treated as that session's initial. That is deliberate — the earlier fire
 * could not have carried the session id anyway, so suppressing the later one
 * would leave the session with no identifiable initial at all.
 */
let initialFiredForSession: string | null = null

/**
 * Stands in for the session id when Cohesion has not initialized, so the
 * in-memory layer can still tell a repeat from a first fire without pretending
 * to know which session it belongs to.
 */
const UNIDENTIFIED_SESSION = '__unidentified-session__'

/**
 * Read here rather than borrowed from another library, so this lib owns its
 * Cohesion access the way `usePageViewed` does. Returns null before Cohesion
 * initializes, which the gate above treats as an unidentified session.
 */
function getCurrentCohesionSessionId(): string | null {
  if (typeof window === 'undefined') return null
  return window._Cohesion?.sessionId ?? null
}

/**
 * Exported for tests only — module state would otherwise leak between cases.
 */
export function resetUsagePredictionGateForTests(): void {
  initialFiredForSession = null
}

/**
 * The figure last reported in this tab, so a surface that re-reads a stored
 * prediction can tell a refresh from a new value.
 *
 * Not keyed on the Cohesion session id, unlike the gate above: a host that does
 * not match Cohesion's cookie domain gets a new id every load, so a key built
 * from it never matches. See `docs/eventing.md`.
 */
const LAST_REPORTED_KEY = 'strataUsagePredictionLastReported'

function recordReportedUsage(monthlyUsageKwh: number): void {
  try {
    sessionStorage.setItem(LAST_REPORTED_KEY, String(monthlyUsageKwh))
  } catch {
    // Storage blocked — a surface that re-reads will report again.
  }
}

/** False when it cannot be proven, since a figure never reported is the worse loss. */
export function hasReportedUsageThisSession(monthlyUsageKwh: number): boolean {
  try {
    return sessionStorage.getItem(LAST_REPORTED_KEY) === String(monthlyUsageKwh)
  } catch {
    return false
  }
}

/**
 * Exported for tests only. Kept separate from the gate reset on purpose: that
 * one simulates a fresh page load, which this record has to survive.
 */
export function clearReportedUsageForTests(): void {
  try {
    sessionStorage.removeItem(LAST_REPORTED_KEY)
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

/**
 * Reads the average monthly usage figure, in whole kWh.
 *
 * Derived from the annual figure rather than by averaging the model's
 * month-by-month breakdown: the breakdown can be absent while the annual figure
 * is present, and dividing the annual figure is how this project already
 * derives the monthly usage it displays on the grid.
 */
export function readAverageMonthlyUsage(response: unknown): number | null {
  if (typeof response !== 'object' || response === null) return null

  const { success, data } = response as { success?: unknown; data?: unknown }
  if (success !== true) return null
  if (typeof data !== 'object' || data === null) return null

  const { results } = data as { results?: unknown }
  if (typeof results !== 'object' || results === null) return null

  const { annualConsumption } = results as { annualConsumption?: unknown }
  // Zero is a real prediction, not a missing one — only absent or non-finite
  // figures are suppressed.
  if (typeof annualConsumption !== 'number' || !Number.isFinite(annualConsumption)) return null

  return Math.round(annualConsumption / 12)
}

function isUnsuccessfulResponse(response: unknown): boolean {
  if (typeof response !== 'object' || response === null) return true
  return (response as { success?: unknown }).success !== true
}

/**
 * Resolves whether this is the session's first prediction or a later one,
 * without yet spending the gate.
 *
 * Reading and marking are split because the gate must only be consumed by an
 * event that actually reached the collector. `commit` is deliberately the
 * caller's to call, once the beam is confirmed — a dropped beam that had
 * already marked the gate would leave the session's real first estimate
 * labelled a rerun, which is the one shape an analyst cannot recover from.
 *
 * Stores the Cohesion session id rather than a boolean so the gate
 * self-invalidates when the session rotates, matching
 * `comparisonAssistModalSession`.
 */
function resolveUsageSource(): {
  usageSource: UsageSource
  commit: (monthlyUsageKwh: number) => void
} {
  const sessionId = getCurrentCohesionSessionId()
  const sessionKey = sessionId ?? UNIDENTIFIED_SESSION

  // The in-memory layer is keyed by session rather than a bare boolean, so a
  // rotated session re-arms the gate here exactly as it does in storage.
  let alreadyFired = initialFiredForSession === sessionKey

  if (sessionId !== null) {
    try {
      if (sessionStorage.getItem(SESSION_GATE_KEY) === sessionId) {
        alreadyFired = true
      }
    } catch {
      // Storage blocked — the in-memory layer above is the whole answer.
    }
  }

  return {
    usageSource: alreadyFired ? RERUN_SOURCE : INITIAL_SOURCE,
    commit: (monthlyUsageKwh: number) => {
      // A figure the collector never received is one this session has not reported.
      recordReportedUsage(monthlyUsageKwh)

      initialFiredForSession = sessionKey

      if (sessionId === null) return
      try {
        sessionStorage.setItem(SESSION_GATE_KEY, sessionId)
      } catch {
        // Storage blocked — the in-memory layer above is the whole answer.
      }
    },
  }
}

/**
 * Emits the consumption model's predicted usage as a FieldInputted event,
 * reported as the shopper's average monthly kWh.
 *
 * Call this wherever a consumption-prediction response is received, rather than
 * at each caller of the prediction API, so a surface added later cannot be
 * silently uninstrumented.
 *
 * Never throws: a tracking failure must not break a prediction. Returns why it
 * did not emit so the caller can log it.
 *
 * `emitted: true` means Tagular received the row, not merely that this tried —
 * the once-per-session gate is spent only on that confirmation.
 */
export function trackUsagePrediction(
  response: unknown,
  options: TrackUsagePredictionOptions = {},
): UsagePredictionResult {
  const { formId, formName, isStillCurrent } = options

  try {
    if (isUnsuccessfulResponse(response)) {
      return { emitted: false, reason: 'unsuccessful-response' }
    }

    const monthlyUsageKwh = readAverageMonthlyUsage(response)
    if (monthlyUsageKwh === null) {
      return { emitted: false, reason: 'no-usage-value' }
    }

    // Asked before the gate is even read: a response the caller has already
    // moved past describes a figure the shopper never saw.
    if (isStillCurrent && !isStillCurrent()) {
      return { emitted: false, reason: 'superseded' }
    }

    const { usageSource, commit } = resolveUsageSource()

    const beamed = TrackingService.track(
      new FieldInputted({
        formContext: {
          formName: formName || DEFAULT_FORM_NAME,
          formType: FORM_TYPE,
          formId: formId ?? '',
        },
        userInputField: {
          fieldName: USAGE_FIELD_NAME,
          fieldLabel: USAGE_FIELD_LABEL,
          fieldType: 'text',
          // Whole kWh, no separators or unit suffix — downstream consumers parse
          // this.
          fieldValue: String(monthlyUsageKwh),
          fieldSource: usageSource,
          autofilled: true,
        },
      }),
    )

    // Left un-spent on a dropped beam, so the next prediction in this session
    // can still claim the initial slot rather than arriving as a correction to
    // an event that was never sent.
    if (!beamed) {
      return { emitted: false, reason: 'tracking-unavailable' }
    }
    commit(monthlyUsageKwh)

    return { emitted: true, usageSource, monthlyUsageKwh }
  } catch {
    return { emitted: false, reason: 'emitter-error' }
  }
}

export { DEFAULT_FORM_NAME, INITIAL_SOURCE, RERUN_SOURCE, SESSION_GATE_KEY, USAGE_FIELD_NAME }
